/**
 * Work Log Review group-stats table — tasks 159c (rendering) and 159d
 * (Classify wiring).
 *
 * Renders one row per group with the seven sortable columns from locked
 * decision #4 (count, hours, distinctEmployees, firstSeenAt, lastSeenAt,
 * percentOfFiltered) plus the group-key cell and a Classify button that
 * opens the 159d modal via `onClassifyGroup`.
 *
 * Sort is server-side: clicking a header re-issues the request via
 * `onSort`. The page owns the sort state.
 */

import { DataTable } from '@/components/composites/DataTable';
import { Button } from '@/components/primitives/Button';
import { formatDayShort } from '@/lib/dates';
import type { GroupByDimension, SortByColumn, WorkLogReviewGroupRow } from '@/types/workLogReview';
import styles from './WorkLogReviewStatsTable.module.css';

type ColumnKey =
  | 'group'
  | 'count'
  | 'hours'
  | 'distinctEmployees'
  | 'firstSeenAt'
  | 'lastSeenAt'
  | 'percentOfFiltered'
  | 'confidence'
  | 'actions';

interface WorkLogReviewStatsTableProps {
  rows: readonly WorkLogReviewGroupRow[];
  groupBy: readonly GroupByDimension[];
  sortBy: SortByColumn;
  sortDir: 'asc' | 'desc';
  onSort: (key: SortByColumn) => void;
  /** Opens the Classify modal scoped to a single group. */
  onClassifyGroup: (row: WorkLogReviewGroupRow) => void;
}

function renderGroupCell(row: WorkLogReviewGroupRow, groupBy: readonly GroupByDimension[]): string {
  if (groupBy.length === 0) return 'All filtered events';
  const parts = groupBy.map((dim) => row.key[dim] ?? '(none)');
  return parts.join(' › ');
}

export function WorkLogReviewStatsTable({
  rows,
  groupBy,
  sortBy,
  sortDir,
  onSort,
  onClassifyGroup,
}: WorkLogReviewStatsTableProps) {
  const sort = { key: sortBy as ColumnKey, direction: sortDir };

  return (
    <DataTable<WorkLogReviewGroupRow, ColumnKey>
      caption="Work Log Review group statistics"
      sort={sort}
      onSort={(key) => {
        if (key === 'group' || key === 'actions') return;
        onSort(key as SortByColumn);
      }}
      columns={[
        {
          key: 'group',
          header: groupBy.length === 0 ? 'Filter total' : 'Group',
          render: (r) => <span className={styles.groupCell}>{renderGroupCell(r, groupBy)}</span>,
        },
        {
          key: 'count',
          header: 'Events',
          align: 'right',
          sortable: true,
          render: (r) => <span className={styles.numCell}>{r.count.toLocaleString()}</span>,
        },
        {
          key: 'hours',
          header: 'Hours',
          align: 'right',
          sortable: true,
          render: (r) => <span className={styles.numCell}>{r.hours.toFixed(1)}h</span>,
        },
        {
          key: 'distinctEmployees',
          header: 'People',
          align: 'right',
          sortable: true,
          render: (r) => <span className={styles.numCell}>{r.distinctEmployees}</span>,
        },
        {
          key: 'firstSeenAt',
          header: 'First seen',
          align: 'right',
          sortable: true,
          render: (r) => <span className={styles.numCell}>{formatDayShort(r.firstSeenAt)}</span>,
        },
        {
          key: 'lastSeenAt',
          header: 'Last seen',
          align: 'right',
          sortable: true,
          render: (r) => <span className={styles.numCell}>{formatDayShort(r.lastSeenAt)}</span>,
        },
        {
          key: 'percentOfFiltered',
          header: '% of filtered',
          align: 'right',
          sortable: true,
          render: (r) => <span className={styles.numCell}>{r.percentOfFiltered.toFixed(1)}%</span>,
        },
        {
          // Task 204 D9 — descending = high-confidence first.
          key: 'confidence',
          header: 'Confidence',
          align: 'right',
          sortable: true,
          render: (r) => (
            <span className={styles.numCell}>
              {r.confidence === undefined || r.confidence === null ? '—' : r.confidence.toFixed(2)}
            </span>
          ),
        },
        {
          key: 'actions',
          header: '',
          align: 'right',
          render: (r) => (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onClassifyGroup(r)}
              data-testid="wlr-classify-row"
            >
              Classify
            </Button>
          ),
        },
      ]}
      rows={[...rows]}
      getRowKey={(r) => JSON.stringify(r.key) + ':' + r.firstSeenAt}
      emptyState={
        <span className={styles.empty}>
          No activity matches these filters in the selected window.
        </span>
      }
    />
  );
}
